/**
 * DatePickerIT — Shadcn Calendar wrapped as a Popover trigger with:
 *  - it-IT locale (date-fns)
 *  - Formato "gg/mm/aaaa" nel trigger
 *  - Lunedì come primo giorno della settimana
 *  - Valore serializzato "YYYY-MM-DD" (compat con <input type="date">)
 *
 * Uso:
 *   <DatePickerIT value={form.due_date} onChange={(v) => setForm({...form, due_date: v})}
 *                 placeholder="Seleziona una data" testId="task-due-date" />
 */
import { useState } from "react";
import { it } from "date-fns/locale";
import { format, parseISO, isValid } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

function _toISO(d) {
  if (!d) return "";
  // Local date -> ISO YYYY-MM-DD (senza timezone shift)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function _fromISO(s) {
  if (!s) return undefined;
  try {
    const d = parseISO(s);
    if (isValid(d)) return d;
  } catch {}
  return undefined;
}

export default function DatePickerIT({
  value,
  onChange,
  placeholder = "gg/mm/aaaa",
  disabled = false,
  clearable = true,
  minDate,
  maxDate,
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const selected = _fromISO(value);
  const label = selected ? format(selected, "dd/MM/yyyy", { locale: it }) : "";

  const handleSelect = (d) => {
    if (!d) { onChange?.(""); return; }
    onChange?.(_toISO(d));
    setOpen(false);
  };

  return (
    <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`pxos-input inline-flex items-center gap-2 justify-between w-full text-left ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          data-testid={testId}
          aria-haspopup="dialog"
        >
          <span className={label ? "" : "opacity-50"}>{label || placeholder}</span>
          <span className="inline-flex items-center gap-1">
            {clearable && selected && (
              <span
                role="button"
                tabIndex={-1}
                className="p-0.5 rounded-full hover:bg-black/5"
                onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                aria-label="Rimuovi data"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                <X size={14} className="opacity-70" />
              </span>
            )}
            <CalendarIcon size={14} className="opacity-70" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          locale={it}
          weekStartsOn={1}
          fromDate={minDate}
          toDate={maxDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
